import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState } from "../components/UI";
import { toast } from "sonner";
import {
  ScrollText, Search, BookOpen, CheckCircle2, PenLine, ArrowRight, Filter,
  Sparkles, Users, ShieldCheck, Landmark, HeartHandshake, Lock, HardHat, Cog,
  DollarSign, Bell,
} from "lucide-react";

/*
 * Policies , Workspace acknowledgment library.
 * Strictly a READ + SIGN surface for every role (employee, manager, HR, admin,
 * super admin). Nothing is created here. Authoring lives at /resources under
 * the Organization section.
 */

/* Icon per category for the reader view. Keeps the library visually scannable. */
const CATEGORY_META = {
  "Ethics": { icon: Landmark, tint: "bg-violet-50 text-violet-700 border-violet-200" },
  "People & Culture": { icon: HeartHandshake, tint: "bg-rose-50 text-rose-700 border-rose-200" },
  "Privacy & Security": { icon: Lock, tint: "bg-blue-50 text-blue-700 border-blue-200" },
  "Safety": { icon: HardHat, tint: "bg-amber-50 text-amber-700 border-amber-200" },
  "Operations": { icon: Cog, tint: "bg-slate-100 text-slate-700 border-slate-200" },
  "Compensation & Benefits": { icon: DollarSign, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  "Other": { icon: BookOpen, tint: "bg-slate-100 text-slate-700 border-slate-200" },
};

const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

export default function Policies() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [myAcks, setMyAcks] = useState([]);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Only approved policies are shown on this signing library. Authoring happens
  // under /resources; drafts/pending never appear here.
  const load = async () => {
    try {
      const params = { status: "approved" };
      if (q) params.q = q;
      const { data } = await api.get("/policies", { params });
      setPolicies(data);
    } catch { toast.error("Failed to load policies"); }
  };

  const loadAcks = async () => {
    try {
      const { data } = await api.get("/my/policy-acknowledgments");
      setMyAcks(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadAcks(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const approved = useMemo(() => policies.filter((p) => p.status === "approved"), [policies]);

  const ackedFor = useMemo(() => {
    const map = new Map();
    for (const a of myAcks) {
      if (!map.has(a.policy_id) || a.policy_version > (map.get(a.policy_id)?.policy_version ?? 0)) {
        map.set(a.policy_id, a);
      }
    }
    return map;
  }, [myAcks]);

  const isSignedCurrentVersion = (p) => {
    const a = ackedFor.get(p.id);
    return !!(a && a.policy_fingerprint === p.fingerprint);
  };
  const isSignedOldVersion = (p) => {
    const a = ackedFor.get(p.id);
    return !!(a && a.policy_fingerprint !== p.fingerprint);
  };

  const filtered = useMemo(() => {
    if (categoryFilter === "all") return approved;
    return approved.filter((p) => p.category === categoryFilter);
  }, [approved, categoryFilter]);

  const needsSignature = useMemo(
    () => filtered.filter((p) => !isSignedCurrentVersion(p)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, ackedFor],
  );

  const categoriesInLibrary = useMemo(() => {
    const s = new Set(approved.map((p) => p.category).filter(Boolean));
    return Array.from(s);
  }, [approved]);

  return (
    <div className="pb-12" data-testid="policies-signing-library">
      {/* Hero */}
      <div className="border-b border-slate-200 bg-gradient-to-br from-blue-50/60 via-white to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-center gap-2 text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-3">
            <BookOpen size={12} strokeWidth={2} />
            <span>Policy Library · Read &amp; Sign</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-tight max-w-3xl">
            Read the policies that shape your workplace and sign the ones that need your name.
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl">
            This library is read-only. New policies are authored by your admins under <Link to="/resources" className="underline hover:text-slate-900">Organization &raquo; Resources</Link>. Sign what applies to you here, and every signature is stored with a tamper-evident fingerprint.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[280px] max-w-2xl">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search policies…"
                className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 shadow-sm"
                data-testid="policy-search"
              />
            </div>
            <Link
              to="/profile"
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-white border border-slate-200 hover:border-slate-400 text-sm text-slate-700 rounded-md transition-colors"
              data-testid="policies-my-signed-link"
            >
              <ScrollText size={14} strokeWidth={1.75} />
              <span className="whitespace-nowrap">My signed policies</span>
              <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{myAcks.length}</span>
            </Link>
          </div>

          {/* Category chips */}
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mr-1 flex items-center gap-1">
              <Filter size={10} strokeWidth={2} /> Filter
            </span>
            <CategoryChip active={categoryFilter === "all"} onClick={() => setCategoryFilter("all")} label="All" count={approved.length} />
            {categoriesInLibrary.map((cat) => (
              <CategoryChip
                key={cat}
                active={categoryFilter === cat}
                onClick={() => setCategoryFilter(cat)}
                label={cat}
                count={approved.filter((p) => p.category === cat).length}
                Icon={(CATEGORY_META[cat] || CATEGORY_META.Other).icon}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 space-y-12">
        {/* Priority: needs signature */}
        {needsSignature.length > 0 && (
          <section data-testid="policies-needs-signature">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={16} className="text-amber-700" strokeWidth={2} />
              <div className="eyebrow text-amber-700">Action needed</div>
            </div>
            <SectionHeader
              title={`${needsSignature.length} ${needsSignature.length === 1 ? "policy needs" : "policies need"} your signature`}
              subtitle="Take a couple of minutes to read and acknowledge. You'll get a downloadable PDF of every policy you sign."
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {needsSignature.map((p) => (
                <PolicyCard
                  key={p.id}
                  policy={p}
                  variant="action"
                  ackedOldVersion={isSignedOldVersion(p)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {approved.length === 0 && (
          <EmptyState
            icon={ScrollText}
            title="No policies published yet"
            subtitle={
              <>
                Your organization has not published any policies yet.
                {" "}
                {["hr", "company_admin", "super_admin"].includes(user?.role) && (
                  <>Head to <Link to="/resources" className="underline hover:text-slate-900">Resources</Link> to author one.</>
                )}
              </>
            }
          />
        )}

        {/* Full library */}
        {approved.length > 0 && (
          <section data-testid="policies-library">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen size={16} className="text-blue-700" strokeWidth={2} />
              <div className="eyebrow text-blue-700">Full library</div>
            </div>
            <SectionHeader
              title="Browse all policies"
              subtitle="Every approved policy in the workspace , open one to read the full text or sign it."
              right={
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                  {filtered.length} / {approved.length}
                </div>
              }
            />
            {filtered.length === 0 ? (
              <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 bg-slate-50/50">
                No policies match your filter. Try clearing the search or category.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((p) => (
                  <PolicyCard
                    key={p.id}
                    policy={p}
                    variant={isSignedCurrentVersion(p) ? "signed" : "library"}
                    ackedOldVersion={isSignedOldVersion(p)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Rights strip */}
        <section
          className="mt-8 border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 p-6"
          data-testid="policies-rights-strip"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <RightsCell
              Icon={ShieldCheck}
              tint="text-emerald-700"
              title="Your signatures are tamper-evident"
              body="Every acknowledgment is sealed with a SHA-256 fingerprint that includes the exact policy text you agreed to."
            />
            <RightsCell
              Icon={Users}
              tint="text-blue-700"
              title="Read-and-sign, on your time"
              body="Policies are available 24/7. You can read them fully before signing , no clock is running."
            />
            <RightsCell
              Icon={Sparkles}
              tint="text-violet-700"
              title="Ask if anything is unclear"
              body={<>Not sure what a policy means? File a support ticket or reach out to HR. <Link to="/tickets" className="underline hover:text-slate-900">Open a ticket &rarr;</Link></>}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function CategoryChip({ active, onClick, label, count, Icon }) {
  return (
    <button
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border transition-all rounded-full " +
        (active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
      }
      data-testid={`policy-cat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {Icon && <Icon size={11} strokeWidth={2} />}
      <span>{label}</span>
      <span className={"text-[10px] font-mono " + (active ? "text-slate-300" : "text-slate-400")}>{count}</span>
    </button>
  );
}

function PolicyCard({ policy, variant, ackedOldVersion }) {
  const meta = CATEGORY_META[policy.category] || CATEGORY_META.Other;
  const Icon = meta.icon;
  const summary = policy.summary || stripHtml(policy.content).slice(0, 140);

  const border =
    variant === "action" ? "border-amber-300 bg-white hover:border-amber-500"
    : variant === "signed" ? "border-emerald-200 bg-white hover:border-emerald-400"
    : "border-slate-200 bg-white hover:border-slate-400";

  return (
    <Link
      to={`/policies/${policy.id}`}
      className={"group border p-5 transition-all flex flex-col " + border}
      data-testid={`policy-card-${policy.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className={"w-9 h-9 border flex items-center justify-center shrink-0 " + meta.tint}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        {variant === "action" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5">
            <PenLine size={10} strokeWidth={2} />
            {ackedOldVersion ? "New version" : "Needs signature"}
          </span>
        )}
        {variant === "signed" && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
            <CheckCircle2 size={10} strokeWidth={2} />
            Signed
          </span>
        )}
      </div>
      <div className={"text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1"}>{policy.category}</div>
      <h3 className="font-display text-base font-semibold text-slate-900 mb-1.5 line-clamp-2 group-hover:text-blue-700 transition-colors">
        {policy.name}
      </h3>
      {summary && (
        <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed flex-1">{summary}</p>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[10px] font-mono text-slate-400">
          v{policy.version} · {policy.effective_from ? new Date(policy.effective_from).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 group-hover:text-blue-700 transition-colors">
          {variant === "action" ? "Read & sign" : variant === "signed" ? "View" : "Open"}
          <ArrowRight size={12} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

function RightsCell({ Icon, tint, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={18} className={tint} strokeWidth={1.75} />
      <div>
        <div className="font-display text-sm font-semibold text-slate-900 mb-1">{title}</div>
        <div className="text-xs text-slate-600 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
